import { useState, useMemo } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Lock, CheckCircle2, AlertTriangle } from 'lucide-react';
import api, { formatApiError } from '../lib/api';

export const ResetPassword = () => {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get('token') || '', [params]);
  const navigate = useNavigate();

  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (pw1.length < 8) return setError('Password must be at least 8 characters.');
    if (pw1 !== pw2) return setError('Passwords do not match.');
    setSubmitting(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: pw1 });
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50" data-testid="reset-password-page">
      <Card className="w-full max-w-md border-slate-200 shadow-sm">
        <CardContent className="p-8 space-y-6">
          <div className="flex justify-center mb-2">
            <img src="/csg-logo.png" alt="CSG Security" className="h-10 w-auto" />
          </div>

          {!token ? (
            <div className="text-center space-y-3" data-testid="reset-no-token">
              <div className="w-14 h-14 mx-auto bg-amber-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-7 h-7 text-amber-600" />
              </div>
              <h2 className="text-xl font-heading font-bold text-slate-900">Invalid reset link</h2>
              <p className="text-sm text-slate-500">
                This password reset link is missing a token. Please request a new link.
              </p>
              <Link to="/forgot-password" className="inline-block text-sm text-[#0D5B69] font-semibold hover:underline">
                Request a new link
              </Link>
            </div>
          ) : done ? (
            <div className="text-center space-y-4" data-testid="reset-success">
              <div className="w-14 h-14 mx-auto bg-[#80C030]/15 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7 text-[#0D5B69]" />
              </div>
              <h2 className="text-2xl font-heading font-bold text-slate-900">Password updated</h2>
              <p className="text-sm text-slate-500">
                Redirecting you to sign in...
              </p>
            </div>
          ) : (
            <>
              <div>
                <h2 className="text-2xl font-heading font-bold text-slate-900">Set a new password</h2>
                <p className="text-sm text-slate-500 mt-1">
                  Choose a strong password with at least 8 characters.
                </p>
              </div>

              {error && (
                <Alert variant="destructive" data-testid="reset-error">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="pw1" className="text-xs uppercase tracking-wide text-slate-600">
                    New password
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="pw1"
                      type="password"
                      required
                      autoFocus
                      minLength={8}
                      value={pw1}
                      onChange={(e) => setPw1(e.target.value)}
                      className="pl-9"
                      data-testid="new-password-input"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="pw2" className="text-xs uppercase tracking-wide text-slate-600">
                    Confirm new password
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="pw2"
                      type="password"
                      required
                      minLength={8}
                      value={pw2}
                      onChange={(e) => setPw2(e.target.value)}
                      className="pl-9"
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#80C030] hover:bg-[#6FA528] text-white font-semibold"
                  disabled={submitting}
                  data-testid="reset-submit-btn"
                >
                  {submitting ? 'Updating...' : 'Update password'}
                </Button>
              </form>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
